/**
 * The event's play world layer on the watch screen: the viewer payload's (`world`) to start with, then a newer one
 * whenever the room metadata announces it. Every change to the layer moves the event's `world_layer_version`, which
 * the API publishes as the metadata's `world_version`; a connected viewer whose layer is older fetches
 * `GET /public/events/:id/world` once for that version and swaps it in.
 *
 * Only ever forwards: a response older than the layer it has (a slow request overtaken by a newer one) is dropped, and
 * a failed one keeps the last good layer until the room announces the next version. A reloaded payload with a newer
 * layer takes over too. The same object comes back while the layer is unchanged, so a memoised map passes it by.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { EMPTY_WORLD_LAYER, loadWorldLayer, parseWorldLayer, type WorldLayerPayload } from '@/lib/comrades/worldLayer'

export interface WorldLayerInput {
  eventId: string
  /** The viewer payload's `world`, as the API sent it (read defensively here); absent from an older API. */
  initial: WorldLayerPayload | null | undefined
  /** The room metadata's `world_version` (`useWatchScreen`'s `worldVersion`); null before it says. */
  version: number | null
}

export function useWorldLayer({ eventId, initial, version }: WorldLayerInput): WorldLayerPayload {
  const seed = useMemo(() => parseWorldLayer(initial) ?? EMPTY_WORLD_LAYER, [initial])
  const [state, setState] = useState(() => ({ eventId, layer: seed }))
  // Another event starts from its own payload; a reloaded payload with a newer layer takes over.
  let current = state
  if (state.eventId !== eventId || seed.version > state.layer.version) {
    current = { eventId, layer: seed }
    setState(current)
  }
  const loadedVersion = current.layer.version

  // The version last asked for, so each announced version is fetched once, not on every render.
  const asked = useRef<{ eventId: string; version: number } | null>(null)
  const inFlight = useRef<AbortController | null>(null)
  // Declared before the fetch, so a remount (React's strict mode runs effects twice) asks again.
  useEffect(
    () => () => {
      inFlight.current?.abort()
      inFlight.current = null
      asked.current = null
    },
    [],
  )

  useEffect(() => {
    if (version === null || version <= loadedVersion) return
    if (asked.current?.eventId === eventId && asked.current.version >= version) return
    asked.current = { eventId, version }
    // A newer version supersedes a request still on its way: its answer would be no newer.
    inFlight.current?.abort()
    const controller = new AbortController()
    inFlight.current = controller
    const settle = () => {
      if (inFlight.current === controller) inFlight.current = null
    }
    loadWorldLayer(eventId, controller.signal).then(
      (next) => {
        settle()
        setState((now) => (now.eventId === eventId && next.version > now.layer.version ? { eventId, layer: next } : now))
      },
      // Failed or superseded: the last good layer stays.
      settle,
    )
  }, [eventId, version, loadedVersion])

  return current.layer
}
